/**
 * SolutionPage Component
 * Dynamic page for ICP-specific solutions (Photographers, Doulas, etc.)
 * Optimized for mobile-first, high-conversion landing page
 */

import { lazy, Suspense } from 'react'
import { useParams, Link, Navigate } from 'react-router-dom'
import { ArrowRight, Calendar, CreditCard, FileText, Lightning, Lock, Sparkle, Rocket, Check, Crown, Package } from '@phosphor-icons/react'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { useSEO, getSEOConfig, type SupportedLanguage, type FAQItem } from '@/lib/seo'
import { getSolutionContent, type SolutionSlug } from './content'
import { PinDayLogoIcon } from '@/components/ui/PinDayLogo'

// Lazy load heavy components
const Footer = lazy(() => import('../Footer').then(module => ({ default: module.Footer })))
const Accordion = lazy(() => import('@/components/ui/accordion').then(module => ({ 
  default: module.Accordion 
})))
const AccordionContent = lazy(() => import('@/components/ui/accordion').then(module => ({ 
  default: module.AccordionContent 
})))
const AccordionItem = lazy(() => import('@/components/ui/accordion').then(module => ({ 
  default: module.AccordionItem 
})))
const AccordionTrigger = lazy(() => import('@/components/ui/accordion').then(module => ({ 
  default: module.AccordionTrigger 
})))

interface SolutionPageProps {
  language: SupportedLanguage
  onLanguageChange: (lang: SupportedLanguage) => void
}

const colorClasses = {
  purple: {
    badge: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300',
    button: 'bg-purple-600 hover:bg-purple-700',
    icon: 'text-purple-600',
    border: 'border-purple-200 dark:border-purple-800',
    cta: 'from-purple-600 to-purple-700 dark:from-purple-900 dark:to-purple-950',
    ctaBg: '#9333ea',
  },
  pink: {
    badge: 'bg-pink-100 text-pink-700 dark:bg-pink-900/30 dark:text-pink-300',
    button: 'bg-pink-600 hover:bg-pink-700',
    icon: 'text-pink-600',
    border: 'border-pink-200 dark:border-pink-800',
    cta: 'from-pink-600 to-pink-700 dark:from-pink-900 dark:to-pink-950',
    ctaBg: '#ec4899',
  },
  orange: {
    badge: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300',
    button: 'bg-orange-600 hover:bg-orange-700',
    icon: 'text-orange-600',
    border: 'border-orange-200 dark:border-orange-800',
    cta: 'from-orange-600 to-orange-700 dark:from-orange-900 dark:to-orange-950',
    ctaBg: '#ea580c',
  },
  rose: {
    badge: 'bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300',
    button: 'bg-rose-600 hover:bg-rose-700',
    icon: 'text-rose-600',
    border: 'border-rose-200 dark:border-rose-800',
    cta: 'from-rose-600 to-rose-700 dark:from-rose-900 dark:to-rose-950',
    ctaBg: '#e11d48',
  },
  teal: {
    badge: 'bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-300',
    button: 'bg-teal-600 hover:bg-teal-700',
    icon: 'text-teal-600',
    border: 'border-teal-200 dark:border-teal-800',
    cta: 'from-teal-600 to-teal-700 dark:from-teal-900 dark:to-teal-950',
    ctaBg: '#0d9488',
  },
  blue: {
    badge: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
    button: 'bg-blue-600 hover:bg-blue-700',
    icon: 'text-blue-600',
    border: 'border-blue-200 dark:border-blue-800',
    cta: 'from-blue-600 to-blue-700 dark:from-blue-900 dark:to-blue-950',
    ctaBg: '#2563eb',
  },
  cyan: {
    badge: 'bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-300',
    button: 'bg-cyan-600 hover:bg-cyan-700',
    icon: 'text-cyan-600',
    border: 'border-cyan-200 dark:border-cyan-800',
    cta: 'from-cyan-600 to-cyan-700 dark:from-cyan-900 dark:to-cyan-950',
    ctaBg: '#0891b2',
  },
  emerald: {
    badge: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300',
    button: 'bg-emerald-600 hover:bg-emerald-700',
    icon: 'text-emerald-600',
    border: 'border-emerald-200 dark:border-emerald-800',
    cta: 'from-emerald-600 to-emerald-700 dark:from-emerald-900 dark:to-emerald-950',
    ctaBg: '#059669',
  },
  indigo: {
    badge: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300',
    button: 'bg-indigo-600 hover:bg-indigo-700',
    icon: 'text-indigo-600',
    border: 'border-indigo-200 dark:border-indigo-800',
    cta: 'from-indigo-600 to-indigo-700 dark:from-indigo-900 dark:to-indigo-950',
    ctaBg: '#4f46e5',
  },
  red: {
    badge: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
    button: 'bg-red-600 hover:bg-red-700',
    icon: 'text-red-600',
    border: 'border-red-200 dark:border-red-800',
    cta: 'from-red-600 to-red-700 dark:from-red-900 dark:to-red-950',
    ctaBg: '#dc2626',
  },
  yellow: {
    badge: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300',
    button: 'bg-yellow-600 hover:bg-yellow-700',
    icon: 'text-yellow-600',
    border: 'border-yellow-200 dark:border-yellow-800',
    cta: 'from-yellow-600 to-yellow-700 dark:from-yellow-900 dark:to-yellow-950',
    ctaBg: '#ca8a04',
  },
}

export function SolutionPage({ language, onLanguageChange }: SolutionPageProps) {
  const { slug } = useParams<{ slug: string }>()
  
  // Validate slug - must match all slugs defined in SolutionSlug type
  const validSlugs: SolutionSlug[] = [
    'photographers',
    'doulas',
    'tattoo-artists',
    'wedding-planners',
    'personal-organizers',
    'event-planners',
    'yoga-instructors',
    'fitness-coaches',
    'hair-stylists',
    'nurses',
    'pop-up-decorators',
    'telemedicine-professionals',
    'tarot-readers',
    'reiki-practitioners',
  ]
  
  if (!slug || !validSlugs.includes(slug as SolutionSlug)) {
    return <Navigate to="/" replace />
  }
  
  const solutionSlug = slug as SolutionSlug
  const content = getSolutionContent(solutionSlug, language)
  const seoConfig = getSEOConfig(`solutions-${solutionSlug}`, language)
  const colors = colorClasses[content.color as keyof typeof colorClasses]
  
  // SEO Hook
  useSEO({
    title: seoConfig?.title || content.hero.title,
    description: seoConfig?.description || content.hero.subtitle,
    keywords: seoConfig?.keywords || [],
    language,
    path: `/solutions/${solutionSlug}`,
    breadcrumbs: [
      { name: 'Home', url: 'https://pinday.me/' },
      { name: 'Solutions', url: 'https://pinday.me/solutions' },
      { name: content.hero.badge, url: `https://pinday.me/solutions/${solutionSlug}` },
    ],
    faq: content.faq as FAQItem[],
  })

  return (
    <div className="min-h-screen bg-background">
      {/* Header - Minimal & Mobile-First */}
      <header className="sticky top-0 z-50 border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60" role="banner">
        <div className="container mx-auto px-4 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <span className="text-2xl"><PinDayLogoIcon /></span>
            <span className="font-bold text-lg hidden sm:inline">PinDay</span>
          </Link>
          
          <div className="flex items-center gap-2 sm:gap-4">
            <label htmlFor="language-select" className="sr-only">
              {language === 'pt' ? 'Selecionar idioma' : language === 'es' ? 'Seleccionar idioma' : 'Select language'}
            </label>
            <select 
              id="language-select"
              value={language}
              onChange={(e) => onLanguageChange(e.target.value as SupportedLanguage)}
              className="text-xs sm:text-sm border rounded px-2 py-1"
              aria-label={language === 'pt' ? 'Selecionar idioma' : language === 'es' ? 'Seleccionar idioma' : 'Select language'}
            >
              <option value="pt">PT</option>
              <option value="en">EN</option>
              <option value="es">ES</option>
            </select>
            
            <Link to="/login" className="hidden sm:block">
              <Button variant="ghost" size="sm">
                {language === 'pt' ? 'Entrar' : language === 'es' ? 'Entrar' : 'Login'}
              </Button>
            </Link>
            
            <Link to="/register">
              <Button size="sm" className={`${colors.button} text-xs sm:text-sm`}>
                {language === 'pt' ? 'Começar' : language === 'es' ? 'Comenzar' : 'Start'}
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Hero - Mobile First Stack */}
      <main>
      <section className="py-12 sm:py-20 bg-gradient-to-b from-muted/50 to-background">
        <div className="container mx-auto px-4">
          {/* Mobile: Image first for visual impact */}
          <div className="sm:hidden mb-8">
            <div className="relative rounded-2xl overflow-hidden shadow-xl">
              <img
                src={content.images.hero.src}
                alt={content.images.hero.alt}
                className="w-full h-64 object-cover"
                loading="eager"
              />
              {content.images.hero.caption && (
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-3">
                  <p className="text-white text-xs sm:text-sm">{content.images.hero.caption}</p>
                </div>
              )}
            </div>
          </div>

          {/* Content Grid - 2 col on desktop */}
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            {/* Text Content */}
            <div className="text-center sm:text-left order-2 sm:order-1">
              <span className={`inline-block px-3 sm:px-4 py-1 sm:py-2 rounded-full text-xs sm:text-sm font-medium mb-4 sm:mb-6 ${colors.badge}`}>
                {content.icon} {content.hero.badge}
              </span>
              
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4 sm:mb-6 leading-tight">
                {content.hero.title}
              </h1>
              
              <p className="text-base sm:text-lg text-muted-foreground mb-6 sm:mb-8 leading-relaxed">
                {content.hero.subtitle}
              </p>
              
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center sm:justify-start">
                <Link to="/register" className="order-1">
                  <Button size="lg" className={`${colors.button} w-full sm:w-auto gap-2 text-sm sm:text-base`}>
                    {content.hero.cta}
                    <ArrowRight size={18} />
                  </Button>
                </Link>
                <Link to="/pricing" className="order-2">
                  <Button size="lg" variant="outline" className="w-full sm:w-auto text-sm sm:text-base">
                    {language === 'pt' ? 'Ver preços' : language === 'es' ? 'Ver precios' : 'View pricing'}
                  </Button>
                </Link>
              </div>
            </div>

            {/* Hero Image - Desktop only */}
            <div className="hidden sm:block relative order-1 sm:order-2">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src={content.images.hero.src}
                  alt={content.images.hero.alt}
                  className="w-full h-auto object-cover"
                  loading="eager"
                />
                {content.images.hero.caption && (
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-4">
                    <p className="text-white text-sm">{content.images.hero.caption}</p>
                  </div>
                )}
              </div>
              {/* Decorative elements */}
              <div className={`absolute -z-10 -top-4 -right-4 w-72 h-72 bg-${content.color}-200/30 dark:bg-${content.color}-800/20 rounded-full blur-3xl`} />
            </div>
          </div>
        </div>
      </section>

      {/* What is PinDay - Value Proposition */}
      <section className="py-16 sm:py-20 bg-white dark:bg-slate-950">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-2xl sm:text-3xl font-bold text-center mb-4">
              {language === 'pt' 
                ? 'O que é PinDay?' 
                : language === 'es' 
                ? '¿Qué es PinDay?' 
                : 'What is PinDay?'}
            </h2>
            <p className="text-center text-muted-foreground text-sm sm:text-base mb-8 sm:mb-12">
              {language === 'pt'
                ? 'PinDay é uma plataforma que transforma como você vende seus serviços. Crie um link, compartilhe, e seus clientes reservam com sinal que bloqueia a data automaticamente.'
                : language === 'es'
                ? 'PinDay es una plataforma que transforma cómo vendes tus servicios. Crea un enlace, comparte, y tus clientes reservan con seña que bloquea la fecha automáticamente.'
                : 'PinDay is a platform that transforms how you sell your services. Create a link, share it, and your clients book with a deposit that automatically blocks the date.'}
            </p>

            <div className="grid sm:grid-cols-3 gap-4">
              <div className="p-4 sm:p-6 rounded-lg bg-muted/50 text-center">
                <div className="text-3xl sm:text-4xl mb-2 flex justify-center"><Lightning size={48} className={colors.icon} /></div>
                <h3 className="font-semibold text-sm sm:text-base mb-2">
                  {language === 'pt' ? 'Rápido' : language === 'es' ? 'Rápido' : 'Fast'}
                </h3>
                <p className="text-xs sm:text-sm text-muted-foreground">
                  {language === 'pt' 
                    ? 'Setup em minutos, não horas' 
                    : language === 'es'
                    ? 'Configuración en minutos'
                    : 'Setup in minutes'}
                </p>
              </div>
              <div className="p-4 sm:p-6 rounded-lg bg-muted/50 text-center">
                <div className="text-3xl sm:text-4xl mb-2 flex justify-center"><Lock size={48} className={colors.icon} /></div>
                <h3 className="font-semibold text-sm sm:text-base mb-2">
                  {language === 'pt' ? 'Seguro' : language === 'es' ? 'Seguro' : 'Secure'}
                </h3>
                <p className="text-xs sm:text-sm text-muted-foreground">
                  {language === 'pt'
                    ? 'Contrato digital incluso'
                    : language === 'es'
                    ? 'Contrato digital incluido'
                    : 'Digital contract included'}
                </p>
              </div>
              <div className="p-4 sm:p-6 rounded-lg bg-muted/50 text-center">
                <div className="text-3xl sm:text-4xl mb-2 flex justify-center"><Crown size={48} className={colors.icon} /></div>
                <h3 className="font-semibold text-sm sm:text-base mb-2">
                  {language === 'pt' ? 'Profissional' : language === 'es' ? 'Profesional' : 'Pro'}
                </h3>
                <p className="text-xs sm:text-sm text-muted-foreground">
                  {language === 'pt'
                    ? 'Pareça maior do que é'
                    : language === 'es'
                    ? 'Parece más grande de lo que eres'
                    : 'Look bigger than you are'}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why PinDay - Core Benefits */}
      <section className="py-16 sm:py-20">
        <div className="container mx-auto px-4">
          <h2 className="text-2xl sm:text-3xl font-bold text-center mb-3 sm:mb-4">
            {language === 'pt' ? 'Por que usar PinDay?' : language === 'es' ? '¿Por qué usar PinDay?' : 'Why PinDay?'}
          </h2>
          <p className="text-center text-muted-foreground text-sm sm:text-base mb-10 sm:mb-12 max-w-2xl mx-auto">
            {language === 'pt'
              ? 'Você merece um sistema que trabalhe para você, não contra você.'
              : language === 'es'
              ? 'Mereces un sistema que trabaje para ti, no en tu contra.'
              : 'You deserve a system that works for you, not against you.'}
          </p>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {[
              {
                icon: <Lightning size={24} className={colors.icon} />,
                title: language === 'pt' ? 'Sinal que bloqueia data' : language === 'es' ? 'Seña que bloquea fecha' : 'Deposit blocks date',
                desc: language === 'pt' 
                  ? 'Assim que o cliente paga, a data sai de circulação. Automático.'
                  : language === 'es'
                  ? 'Tan pronto como el cliente paga, la fecha se bloquea automáticamente.'
                  : 'As soon as the client pays, the date is blocked automatically.'
              },
              {
                icon: <CreditCard size={24} className={colors.icon} />,
                title: language === 'pt' ? 'Pix + Cartão' : language === 'es' ? 'Transferencia + Tarjeta' : 'Pix + Card',
                desc: language === 'pt'
                  ? 'Seus clientes pagam como preferem. Você recebe tudo na conta.'
                  : language === 'es'
                  ? 'Tus clientes pagan como prefieren. Tú recibes todo en tu cuenta.'
                  : 'Your clients pay as they prefer. You receive everything in your account.'
              },
              {
                icon: <FileText size={24} className={colors.icon} />,
                title: language === 'pt' ? 'Contrato digital' : language === 'es' ? 'Contrato digital' : 'Digital contract',
                desc: language === 'pt'
                  ? 'Proteja-se com contrato automático e política de cancelamento.'
                  : language === 'es'
                  ? 'Protégete con contrato automático y política de cancelación.'
                  : 'Protect yourself with automatic contracts and cancellation policies.'
              },
              {
                icon: <Calendar size={24} className={colors.icon} />,
                title: language === 'pt' ? 'Google Calendar integrado' : language === 'es' ? 'Google Calendar integrado' : 'Google Calendar sync',
                desc: language === 'pt'
                  ? 'Suas reservas no PinDay aparecem direto no seu Google Calendar.'
                  : language === 'es'
                  ? 'Tus reservas en PinDay aparecen directamente en tu Google Calendar.'
                  : 'Your PinDay bookings sync with your Google Calendar.'
              },
              {
                icon: <Lock size={24} className={colors.icon} />,
                title: language === 'pt' ? 'Link único e seguro' : language === 'es' ? 'Enlace único y seguro' : 'Unique, secure link',
                desc: language === 'pt'
                  ? 'Compartilhe um link profissional. Você controla quem vê o quê.'
                  : language === 'es'
                  ? 'Comparte un enlace profesional. Tú controlas quién ve qué.'
                  : 'Share a professional link. You control who sees what.'
              },
              {
                icon: <Sparkle size={24} className={colors.icon} />,
                title: language === 'pt' ? 'Sem complicação' : language === 'es' ? 'Sin complicaciones' : 'No complexity',
                desc: language === 'pt'
                  ? 'Sem taxas escondidas, sem contrato, sem burocracia. Simples.'
                  : language === 'es'
                  ? 'Sin tarifas ocultas, sin contrato, sin burocracia. Simple.'
                  : 'No hidden fees, no contracts, no bureaucracy. Simple.'
              },
            ].map((benefit, index) => (
              <Card key={index} className={`${colors.border} border-opacity-50 hover:shadow-lg transition-shadow`}>
                <CardContent className="pt-6">
                  <div className="mb-4">{benefit.icon}</div>
                  <h3 className="font-semibold text-base sm:text-lg mb-2">{benefit.title}</h3>
                  <p className="text-muted-foreground text-sm leading-relaxed">{benefit.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery Section - Only show if there are gallery images */}
      {content.images.gallery.length > 0 && (
        <section className="py-16 sm:py-20 bg-muted/30">
          <div className="container mx-auto px-4">
            <h2 className="text-2xl sm:text-3xl font-bold text-center mb-3 sm:mb-4">
              {language === 'pt' ? 'Como divulgar seus serviços' : language === 'es' ? 'Cómo promover tus servicios' : 'How to showcase your services'}
            </h2>
            <p className="text-center text-muted-foreground text-sm sm:text-base mb-10 sm:mb-12 max-w-2xl mx-auto">
              {language === 'pt' 
                ? 'Veja como você pode apresentar seus serviços com profissionalismo e facilidade usando o PinDay.'
                : language === 'es'
                ? 'Mira cómo puedes presentar tus servicios de manera profesional y fácil usando PinDay.'
                : 'See how you can professionally showcase your services with PinDay.'}
            </p>
            
            <div className="grid sm:grid-cols-2 gap-4 sm:gap-6 max-w-4xl mx-auto">
              {content.images.gallery.map((image, index) => (
                <div key={index} className="group relative rounded-xl overflow-hidden shadow-lg transition-all duration-300 hover:shadow-2xl hover:scale-105">
                  <img
                    src={image.src}
                    alt={image.alt}
                    className="w-full h-60 sm:h-72 object-cover"
                    loading="lazy"
                  />
                  {image.caption && (
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-4">
                      <p className="text-white text-sm sm:text-base font-medium">{image.caption}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Example Packages - Showcase as examples */}
      <section className="py-16 sm:py-20">
        <div className="container mx-auto px-4">
          <h2 className="text-2xl sm:text-3xl font-bold text-center mb-3 sm:mb-4">
            {language === 'pt' ? 'Seus pacotes ficam assim' : language === 'es' ? 'Así se ven tus paquetes' : 'Your packages look like this'}
          </h2>
          <p className="text-center text-muted-foreground text-sm sm:text-base mb-10 sm:mb-12 max-w-2xl mx-auto">
            {language === 'pt' 
              ? 'Cada cliente vê seus pacotes, descrição clara, e um botão para reservar com sinal.'
              : language === 'es'
              ? 'Cada cliente ve tus paquetes, descripción clara, y un botón para reservar con seña.'
              : 'Each client sees your packages, clear description, and a button to book with deposit.'}
          </p>
          
          {/* Dashed Border Container - Simulation Indicator */}
          <div className="border-2 border-dashed border-muted-foreground/30 rounded-3xl p-6 sm:p-8 bg-gradient-to-br from-muted/10 to-transparent relative">
            {/* Simulation Badge */}
            <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 bg-background px-4 py-1.5 rounded-full border-2 border-dashed border-muted-foreground/30">
              <span className="text-xs font-semibold text-muted-foreground">
                {language === 'pt' ? '📦 Simulação' : language === 'es' ? '📦 Simulación' : '📦 Simulation'}
              </span>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8 max-w-5xl mx-auto">
              {content.packages.map((pkg, index) => {
                // Generate default placeholder image URL
                const placeholderImages = [
                  'https://placehold.co/500x300/e2e8f0/64748b?text=Package+1',
                  'https://placehold.co/500x300/e2e8f0/64748b?text=Package+2',
                  'https://placehold.co/500x300/e2e8f0/64748b?text=Package+3',
                ]
                const imageUrl = placeholderImages[index % placeholderImages.length]

                return (
                  <div 
                    key={index}
                    className={`rounded-2xl overflow-hidden transition-all duration-300 hover:shadow-xl hover:-translate-y-2 ${
                      index === 1 
                        ? `shadow-lg ring-2 ${colors.border}` 
                        : 'shadow-md hover:shadow-lg'
                    }`}
                  >
                    {/* Package Image - Placeholder */}
                    <div className="relative w-full h-48 sm:h-56 overflow-hidden bg-muted">
                      <img
                        src={imageUrl}
                        alt={pkg.name}
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent" />
                    </div>

                    {/* Card Header with Premium Styling */}
                    <div className={`p-6 sm:p-8 ${index === 1 ? colors.badge : 'bg-gradient-to-br from-muted/50 to-background'}`}>
                      {index === 1 && (
                        <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold mb-4 ${colors.badge}`}>
                          <Sparkle size={14} weight="fill" />
                          {language === 'pt' ? 'Mais popular' : language === 'es' ? 'Más popular' : 'Most popular'}
                        </div>
                      )}
                      <h3 className={`font-bold text-xl sm:text-2xl mb-2 ${index === 1 ? 'text-black' : 'text-foreground'}`}>
                        {pkg.name}
                      </h3>
                      <p className={`text-sm leading-relaxed ${index === 1 ? 'text-muted-foreground' : 'text-muted-foreground'}`}>
                        {pkg.description}
                      </p>
                    </div>

                    {/* Card Body */}
                    <div className="px-6 sm:px-8 py-6 sm:py-8 bg-card border-t border-border/50">
                      {/* CTA Button - Premium Styling */}
                      <button className={`w-full py-3 px-4 rounded-lg font-semibold text-sm transition-all duration-200 flex items-center justify-center gap-2 mb-4 ${
                        index === 1
                          ? `${colors.button} text-white hover:shadow-lg transform hover:scale-105`
                          : `border-2 ${colors.border} ${colors.icon} hover:bg-muted/30`
                      }`}>
                        <Package size={16} />
                        {language === 'pt' ? 'Contratar' : language === 'es' ? 'Contratar' : 'Book now'}
                      </button>

                      {/* Customization Note - Premium Badge */}
                      <p className="text-center text-xs text-muted-foreground flex items-center justify-center gap-1">
                        <Sparkle size={12} className={colors.icon} />
                        {language === 'pt' 
                          ? 'Personalizável' 
                          : language === 'es'
                          ? 'Personalizable'
                          : 'Customizable'}
                      </p>
                    </div>
                  </div>
                )
              })}
            </div>
          </div>

          <div className="mt-10 sm:mt-14 p-6 sm:p-8 rounded-2xl bg-gradient-to-br from-[var(--pd-primary)]/5 to-[var(--pd-primary)]/10 border-2 border-[var(--pd-primary)]/20 dark:border-[var(--pd-primary)]/30">
            <p className="text-center text-sm sm:text-base font-medium flex items-center justify-center gap-2 flex-wrap">
              <Sparkle size={18} className={colors.icon} weight="fill" />
              <span className="font-semibold">
                {language === 'pt'
                  ? 'Você personaliza tudo: títulos, descrições, preços, imagens'
                  : language === 'es'
                  ? 'Personalizas todo: títulos, descripciones, precios, imágenes'
                  : 'You customize everything: titles, descriptions, prices, images'}
              </span>
            </p>
          </div>
        </div>
      </section>

      {/* Testimonial */}
      {content.testimonial && (
        <section className="py-16 sm:py-20 bg-muted/30">
          <div className="container mx-auto px-4">
            <Card className="max-w-2xl mx-auto border-0 shadow-lg">
              <CardContent className="pt-8 sm:pt-12 pb-8 sm:pb-12">
                <div className="flex justify-center mb-4"><Sparkle size={40} className={colors.icon} /></div>
                <blockquote className="text-lg sm:text-xl font-medium italic text-center mb-6 leading-relaxed">
                  "{content.testimonial.quote}"
                </blockquote>
                <div className="text-center">
                  {content.testimonial.avatar ? (
                    <img 
                      src={content.testimonial.avatar} 
                      alt={content.testimonial.author}
                      className="w-12 h-12 rounded-full mx-auto mb-3 object-cover"
                      loading="lazy"
                    />
                    ) : (
                    <div className={`w-12 h-12 rounded-full mx-auto mb-3 ${colors.badge} flex items-center justify-center`}>
                      <span className="text-lg font-semibold">
                      {content.testimonial.author.charAt(0)}
                      </span>
                    </div>
                  )}
                  <p className="font-semibold text-base sm:text-lg">{content.testimonial.author}</p>
                  <p className="text-sm text-muted-foreground">{content.testimonial.role}</p>
                </div>
              </CardContent>
            </Card>
          </div>
        </section>
      )}

      {/* FAQ */}
      <section className="py-16 sm:py-20">
        <div className="container mx-auto px-4">
          <h2 className="text-2xl sm:text-3xl font-bold text-center mb-3 sm:mb-4">
            {language === 'pt' ? 'Dúvidas frequentes' : language === 'es' ? 'Preguntas frecuentes' : 'Frequently asked questions'}
          </h2>
          
          <Suspense fallback={<div className="h-64 animate-pulse bg-muted/30 rounded-lg max-w-3xl mx-auto"></div>}>
            <Accordion type="single" collapsible className="max-w-3xl mx-auto">
              {content.faq.map((item, index) => (
                <AccordionItem key={index} value={`item-${index}`}>
                  <AccordionTrigger className="text-left text-sm sm:text-base hover:no-underline hover:text-primary py-4 sm:py-5">
                    {item.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-sm sm:text-base text-muted-foreground pb-4">
                    {item.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Suspense>
        </div>
      </section>

      {/* Final CTA - Magnetic & Clear */}
      <section 
        style={{
          background: `linear-gradient(to right, ${colors.ctaBg}, ${colors.ctaBg}dd)`
        }}
        className={`py-16 sm:py-24 ${colors.cta}`}
      >
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-2xl sm:text-4xl font-bold mb-3 sm:mb-4 text-white leading-tight flex items-center justify-center gap-3">
            <Rocket size={32} weight="fill" />
            {language === 'pt' 
              ? 'Comece agora, sem cartão de crédito'
              : language === 'es'
              ? 'Empieza ahora, sin tarjeta de crédito'
              : 'Start now, no credit card needed'}
          </h2>
          <p className="text-base sm:text-lg text-white/90 mb-8 sm:mb-10 max-w-2xl mx-auto leading-relaxed">
            {language === 'pt'
              ? 'Configure seu primeiro pacote em menos de 5 minutos. Comece a receber sinais hoje mesmo.'
              : language === 'es'
              ? 'Configura tu primer paquete en menos de 5 minutos. Empieza a recibir señas hoy.'
              : 'Set up your first package in less than 5 minutes. Start receiving deposits today.'}
          </p>
          <Link to="/register">
            <Button size="lg" variant="secondary" className="text-base sm:text-lg gap-2 px-8 sm:px-10 h-12 sm:h-14">
              {content.hero.cta}
              <ArrowRight size={20} />
            </Button>
          </Link>
          <p className="text-white/70 text-xs sm:text-sm mt-6 flex items-center justify-center gap-4 flex-wrap">
            <span className="flex items-center gap-1"><Check size={16} weight="bold" /> {language === 'pt' ? '100% grátis' : language === 'es' ? '100% gratis' : '100% free'}</span>
            <span className="flex items-center gap-1"><Check size={16} weight="bold" /> {language === 'pt' ? 'Sem compromisso' : language === 'es' ? 'Sin compromiso' : 'No commitment'}</span>
            <span className="flex items-center gap-1"><Check size={16} weight="bold" /> {language === 'pt' ? 'Cancele quando quiser' : language === 'es' ? 'Cancela cuando quieras' : 'Cancel anytime'}</span>
          </p>
        </div>
      </section>
      </main>

      <Suspense fallback={<div className="h-32 bg-muted/30"></div>}>
        <Footer language={language} />
      </Suspense>
    </div>
  )
}

export default SolutionPage
